import { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react"
import SignaturePadLib from "signature_pad"
import { Button } from "@/components/shadcn/ui/button"
import { Eraser, Undo2 } from "lucide-react"

interface SignaturePadProps {
  onSignatureChange?: (isEmpty: boolean) => void
  width?: number
  height?: number
}

export interface SignaturePadRef {
  toDataURL: () => string
  isEmpty: () => boolean
  clear: () => void
}

const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  ({ onSignatureChange, width = 400, height = 200 }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const signaturePadRef = useRef<SignaturePadLib | null>(null)

    const onSignatureChangeRef = useRef(onSignatureChange)
    onSignatureChangeRef.current = onSignatureChange

    useEffect(() => {
      if (!canvasRef.current) return

      const canvas = canvasRef.current
      const ratio = Math.max(window.devicePixelRatio || 1, 1)

      canvas.width = width * ratio
      canvas.height = height * ratio
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`

      const context = canvas.getContext("2d")
      if (context) {
        context.scale(ratio, ratio)
      }

      signaturePadRef.current = new SignaturePadLib(canvas, {
        backgroundColor: "rgb(255, 255, 255)",
        penColor: "rgb(0, 0, 0)",
      })

      const handleEndStroke = () => {
        if (signaturePadRef.current && onSignatureChangeRef.current) {
          onSignatureChangeRef.current(signaturePadRef.current.isEmpty())
        }
      }

      signaturePadRef.current.addEventListener("endStroke", handleEndStroke)

      // Announce the freshly-constructed (empty) pad so consumers cannot keep a
      // stale "non-empty" belief after a remount (e.g. reopening the dialog).
      onSignatureChangeRef.current?.(true)

      return () => {
        if (signaturePadRef.current) {
          signaturePadRef.current.removeEventListener("endStroke", handleEndStroke)
          signaturePadRef.current.off()
        }
      }
    }, [width, height])

    const handleClear = () => {
      signaturePadRef.current?.clear()
      onSignatureChange?.(true)
    }

    const handleUndo = () => {
      const data = signaturePadRef.current?.toData()
      if (data && data.length > 0) {
        data.pop()
        signaturePadRef.current?.fromData(data)
        onSignatureChange?.(signaturePadRef.current?.isEmpty() ?? true)
      }
    }

    const toDataURL = useCallback((): string => {
      return signaturePadRef.current?.toDataURL("image/png") ?? ""
    }, [])

    const isEmpty = useCallback((): boolean => {
      return signaturePadRef.current?.isEmpty() ?? true
    }, [])

    const clear = useCallback((): void => {
      signaturePadRef.current?.clear()
    }, [])

    useImperativeHandle(ref, () => ({
      toDataURL,
      isEmpty,
      clear,
    }))

    return (
      <div className="flex flex-col gap-2">
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white">
          <canvas
            ref={canvasRef}
            className="touch-none"
            style={{ width: `${width}px`, height: `${height}px` }}
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleUndo}>
            <Undo2 className="mr-1 h-4 w-4" />
            Undo
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleClear}>
            <Eraser className="mr-1 h-4 w-4" />
            Clear
          </Button>
        </div>
        <p className="text-xs text-gray-500">Draw your signature above using mouse or touch</p>
      </div>
    )
  },
)

SignaturePad.displayName = "SignaturePad"

export default SignaturePad
